
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_BACKEND_BASE_URL ||
  "http://localhost:5000";

// =====================================================
// HELPER
// =====================================================

const getPostalCode = (address = {}) => {
  return (
    address.postalCode ||
    address.postcode ||
    address.pincode ||
    address.pinCode ||
    address.zipCode ||
    address.zipcode ||
    ""
  );
};

// =====================================================
// GEOLOCATION
// =====================================================

export const fetchGeolocation = createAsyncThunk(
  "search/fetchGeolocation",
  async (_, { rejectWithValue }) => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        rejectWithValue("Geolocation not supported");
        reject("Geolocation not supported");
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } =
              position.coords;

            const res = await axios.get(
              "https://nominatim.openstreetmap.org/reverse",
              {
                params: {
                  lat: latitude,
                  lon: longitude,
                  format: "json",
                  addressdetails: 1,
                },
                headers: {
                  Accept: "application/json",
                },
              }
            );

            const address =
              res.data?.address || {};

            const normalizedPostcode =
              getPostalCode(address);

            const normalizedCity =
              address.city ||
              address.town ||
              address.municipality ||
              address.village ||
              address.suburb ||
              "";

            const normalizedVillage =
              address.village ||
              address.suburb ||
              address.locality ||
              "";

            const normalizedDistrict =
              address.state_district ||
              address.district ||
              address.county ||
              "";

            const locationData = {
              lat: latitude,
              lon: longitude,

              country:
                address.country || "",

              state:
                address.state || "",

              district:
                normalizedDistrict,

              city:
                normalizedCity,

              village:
                normalizedVillage,

              area:
                address.suburb ||
                address.locality ||
                address.neighbourhood ||
                "",

              // IMPORTANT:
              // Keep both names for compatibility
              postcode:
                normalizedPostcode,

              postalCode:
                normalizedPostcode,

              pincode:
                normalizedPostcode,

              fullAddress:
                res.data?.display_name || "",
            };

            console.log(
              "📍 NORMALIZED GEOLOCATION:",
              locationData
            );

            resolve(locationData);
          } catch (error) {
            console.error(
              "❌ Reverse geolocation error:",
              error
            );

            reject(
              "Failed to fetch location data"
            );
          }
        },

        (error) => {
          reject(
            error?.message ||
              "Unable to access your location"
          );
        },

        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000,
        }
      );
    });
  }
);

// =====================================================
// SEARCH SUGGESTIONS
// =====================================================

export const fetchSuggestions =
  createAsyncThunk(
    "search/fetchSuggestions",

    async (
      { q, lat, lon },
      { rejectWithValue }
    ) => {
      try {
        const query = q?.trim();

        if (!query || query.length < 2) {
          return [];
        }

        const params = {
          q: query,
        };

        if (
          lat !== undefined &&
          lat !== null &&
          lat !== ""
        ) {
          params.lat = lat;
        }

        if (
          lon !== undefined &&
          lon !== null &&
          lon !== ""
        ) {
          params.lon = lon;
        }

        const res = await axios.get(
          `${BASE_URL}/business/suggestions`,
          {
            params,
          }
        );

        console.log(
          "🔎 SUGGESTIONS RESPONSE:",
          res.data
        );

        return (
          res.data?.suggestions || []
        );
      } catch (error) {
        console.error(
          "❌ Suggestions error:",
          error
        );

        return rejectWithValue(
          error.response?.data ||
            "Error fetching suggestions"
        );
      }
    }
  );

// =====================================================
// SEARCH USERS
// =====================================================

export const searchUsers =
  createAsyncThunk(
    "search/searchUsers",

    async (
      {
        input,
        country,
        state,
        district,
        city,
        village,
        area,
        postalCode,
        postcode,
        pincode,
        pinCode,
        zipCode,
        lat,
        lon,
      },
      { rejectWithValue }
    ) => {
      try {
        const params = {};

        // =================================================
        // LOCATION
        // =================================================

        if (country) {
          params.country = country;
        }

        if (state) {
          params.state = state;
        }

        if (district) {
          params.district = district;
        }

        if (city) {
          params.city = city;
        }

        if (village) {
          params.village = village;
        }

        if (area) {
          params.area = area;
        }

        // =================================================
        // NORMALIZE PINCODE
        // =================================================

        const normalizedPostalCode =
          postalCode ||
          postcode ||
          pincode ||
          pinCode ||
          zipCode ||
          "";

        if (normalizedPostalCode) {
          params.postalCode =
            normalizedPostalCode;

          // Send these too for backend compatibility
          params.postcode =
            normalizedPostalCode;

          params.pincode =
            normalizedPostalCode;
        }

        // =================================================
        // LAT / LON
        // =================================================

        if (
          lat !== undefined &&
          lat !== null &&
          lat !== ""
        ) {
          params.lat = lat;
        }

        if (
          lon !== undefined &&
          lon !== null &&
          lon !== ""
        ) {
          params.lon = lon;
        }

        // =================================================
        // SEARCH INPUT
        // =================================================

        if (input) {
          const searchValue =
            String(input).trim();

          if (
            /^\d{6,15}$/.test(
              searchValue
            )
          ) {
            params.phone =
              searchValue;
          } else if (
            /@/.test(searchValue)
          ) {
            params.email =
              searchValue;
          } else {
            params.fullName =
              searchValue;
          }
        }

        console.log(
          "🔎 SEARCH API PARAMS:",
          params
        );

        const res = await axios.get(
          `${BASE_URL}/business/searchBusinessUsers`,
          {
            params,
          }
        );

        console.log(
          "✅ SEARCH API RESPONSE:",
          res.data
        );

        const users =
          res.data?.users ||
          res.data?.results ||
          res.data?.data ||
          [];

        // =================================================
        // NORMALIZE USER LOCATION
        // =================================================

        const normalizedUsers =
          Array.isArray(users)
            ? users.map((user) => {
                const address =
                  user.address ||
                  user.location ||
                  {};

                const userPostalCode =
                  getPostalCode(
                    user
                  ) ||
                  getPostalCode(
                    address
                  );

                return {
                  ...user,

                  // Preserve original fields
                  postalCode:
                    userPostalCode,

                  postcode:
                    userPostalCode,

                  pincode:
                    userPostalCode,

                  pinCode:
                    userPostalCode,

                  zipCode:
                    userPostalCode,

                  // Normalize location fields
                  city:
                    user.city ||
                    address.city ||
                    "",

                  village:
                    user.village ||
                    address.village ||
                    "",

                  area:
                    user.area ||
                    address.area ||
                    address.locality ||
                    address.suburb ||
                    "",

                  district:
                    user.district ||
                    address.district ||
                    "",

                  state:
                    user.state ||
                    address.state ||
                    "",

                  country:
                    user.country ||
                    address.country ||
                    "",

                  // Keep address object
                  address: user.address ||
                    address,
                };
              })
            : [];

        console.log(
          "📍 NORMALIZED SEARCH USERS:",
          normalizedUsers
        );

        return normalizedUsers;
      } catch (error) {
        console.error(
          "❌ Search users error:",
          error
        );

        return rejectWithValue(
          error.response?.data ||
            "Error searching users"
        );
      }
    }
  );

// =====================================================
// FETCH REGISTERED LOCATION OPTIONS
// =====================================================

export const fetchLocationOptions =
  createAsyncThunk(
    "search/fetchLocationOptions",

    async (_, { rejectWithValue }) => {
      try {
        console.log(
          "📍 Fetching registered locations..."
        );

        const url =
          `${BASE_URL}/business/search/location-options`;

        console.log(
          "📍 Location API:",
          url
        );

        const res =
          await axios.get(url);

        console.log(
          "📍 Location API response:",
          res.data
        );

        if (!res.data?.success) {
          return rejectWithValue(
            res.data?.message ||
              "Location API failed"
          );
        }

        const postalCodes =
          res.data?.postalCodes ||
          res.data?.postcodes ||
          res.data?.pincodes ||
          [];

        return {
          cities:
            res.data?.cities || [],

          areas:
            res.data?.areas ||
            res.data?.villages ||
            [],

          postalCodes,
        };
      } catch (error) {
        console.error(
          "❌ LOCATION OPTIONS ERROR:",
          error
        );

        return rejectWithValue(
          error.response?.data?.message ||
            error.message ||
            "Failed to load location options"
        );
      }
    }
  );

// =====================================================
// SLICE
// =====================================================

const searchSlice = createSlice({
  name: "search",

  initialState: {
    // =================================================
    // LOCATION
    // =================================================

    country: "",

    state: "",

    district: "",

    city: "",

    village: "",

    area: "",

    // Keep both fields
    postcode: "",

    postalCode: "",

    pincode: "",

    fullAddress: "",

    lat: null,

    lon: null,

    isManual: false,

    isFetchingLocation: false,

    // =================================================
    // SEARCH
    // =================================================

    suggestions: [],

    results: [],

    selectedUser: null,

    // =================================================
    // REGISTERED LOCATION OPTIONS
    // =================================================

    locationOptions: {
      cities: [],
      areas: [],
      postalCodes: [],
    },

    locationOptionsLoading: false,

    locationOptionsError: null,

    error: null,
  },

  reducers: {
    // =================================================
    // SELECT USER
    // =================================================

    setSelectedUser: (
      state,
      action
    ) => {
      const user =
        action.payload || {};

      const address =
        user.address ||
        user.location ||
        {};

      const normalizedPostalCode =
        getPostalCode(user) ||
        getPostalCode(address);

      state.selectedUser = {
        ...user,

        postalCode:
          normalizedPostalCode,

        postcode:
          normalizedPostalCode,

        pincode:
          normalizedPostalCode,
      };
    },

    // =================================================
    // CLEAR USER
    // =================================================

    clearSelectedUser: (
      state
    ) => {
      state.selectedUser = null;
    },

    // =================================================
    // MANUAL LOCATION
    // =================================================

    setManualLocation: (
      state,
      action
    ) => {
      const payload =
        action.payload || {};

      const normalizedPostalCode =
        getPostalCode(payload);

      Object.assign(
        state,
        payload
      );

      state.postcode =
        normalizedPostalCode;

      state.postalCode =
        normalizedPostalCode;

      state.pincode =
        normalizedPostalCode;

      state.isManual = true;
    },

    // =================================================
    // CLEAR SUGGESTIONS
    // =================================================

    clearSuggestions: (
      state
    ) => {
      state.suggestions = [];
    },
  },

  extraReducers: (builder) => {
    builder

      // =================================================
      // GEOLOCATION PENDING
      // =================================================

      .addCase(
        fetchGeolocation.pending,
        (state) => {
          state.isFetchingLocation =
            true;

          state.error = null;
        }
      )

      // =================================================
      // GEOLOCATION SUCCESS
      // =================================================

      .addCase(
        fetchGeolocation.fulfilled,
        (
          state,
          action
        ) => {
          state.isFetchingLocation =
            false;

          if (!state.isManual) {
            const payload =
              action.payload || {};

            Object.assign(
              state,
              payload
            );

            const normalizedPostalCode =
              getPostalCode(
                payload
              );

            state.postcode =
              normalizedPostalCode;

            state.postalCode =
              normalizedPostalCode;

            state.pincode =
              normalizedPostalCode;

            console.log(
              "📍 REDUX LOCATION UPDATED:",
              {
                city:
                  state.city,

                area:
                  state.area,

                postcode:
                  state.postcode,

                postalCode:
                  state.postalCode,

                pincode:
                  state.pincode,

                fullAddress:
                  state.fullAddress,
              }
            );
          }
        }
      )

      // =================================================
      // GEOLOCATION ERROR
      // =================================================

      .addCase(
        fetchGeolocation.rejected,
        (
          state,
          action
        ) => {
          state.isFetchingLocation =
            false;

          state.error =
            action.payload ||
            "Failed to fetch location";
        }
      )

      // =================================================
      // SUGGESTIONS SUCCESS
      // =================================================

      .addCase(
        fetchSuggestions.fulfilled,
        (
          state,
          action
        ) => {
          state.suggestions =
            action.payload || [];
        }
      )

      // =================================================
      // SUGGESTIONS ERROR
      // =================================================

      .addCase(
        fetchSuggestions.rejected,
        (
          state,
          action
        ) => {
          state.suggestions = [];

          state.error =
            action.payload ||
            "Failed to fetch suggestions";
        }
      )

      // =================================================
      // SEARCH USERS PENDING
      // =================================================

      .addCase(
        searchUsers.pending,
        (state) => {
          state.error = null;
        }
      )

      // =================================================
      // SEARCH USERS SUCCESS
      // =================================================

      .addCase(
        searchUsers.fulfilled,
        (
          state,
          action
        ) => {
          state.results =
            action.payload || [];

          console.log(
            "📍 SEARCH RESULTS SAVED TO REDUX:",
            state.results
          );
        }
      )

      // =================================================
      // SEARCH USERS ERROR
      // =================================================

      .addCase(
        searchUsers.rejected,
        (
          state,
          action
        ) => {
          state.results = [];

          state.error =
            action.payload ||
            "Error searching users";
        }
      )

      // =================================================
      // LOCATION OPTIONS PENDING
      // =================================================

      .addCase(
        fetchLocationOptions.pending,
        (state) => {
          state.locationOptionsLoading =
            true;

          state.locationOptionsError =
            null;
        }
      )

      // =================================================
      // LOCATION OPTIONS SUCCESS
      // =================================================

      .addCase(
        fetchLocationOptions.fulfilled,
        (
          state,
          action
        ) => {
          state.locationOptionsLoading =
            false;

          state.locationOptions = {
            cities:
              action.payload?.cities ||
              [],

            areas:
              action.payload?.areas ||
              [],

            postalCodes:
              action.payload
                ?.postalCodes ||
              [],
          };

          console.log(
            "📍 LOCATION OPTIONS SAVED:",
            state.locationOptions
          );
        }
      )

      // =================================================
      // LOCATION OPTIONS ERROR
      // =================================================

      .addCase(
        fetchLocationOptions.rejected,
        (
          state,
          action
        ) => {
          state.locationOptionsLoading =
            false;

          state.locationOptionsError =
            action.payload ||
            "Failed to load locations";
        }
      );
  },
});

// =====================================================
// ACTIONS
// =====================================================

export const {
  setSelectedUser,
  clearSelectedUser,
  setManualLocation,
  clearSuggestions,
} =
  searchSlice.actions;

// =====================================================
// REDUCER
// =====================================================

export default searchSlice.reducer;
